import React, { useState, useEffect } from 'react';
import { Row, Col, message, theme } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  getAutoChargeSettings,
  getBillingDetails,
  getPayments,
  getProducts,
  updateAutoChargeSettings
} from './BillingServices';
import Plans from './Plans/Plans';
import { useGlobal } from '../Shared/GlobalContext/GlobalContext';
import { useTheme } from '../Shared/GlobalContext/ThemeContext';
import { useErrorHandler } from '../../Hooks/useErrorHandler';
import PaymentDetailModal from './PaymentDetailModal/PaymentDetailModal';
import { DEFAULT_PAGINATION } from './constants';
import { formatPayments } from './utils';

// Component imports
import {
  BalanceCard,
  PaymentInfoCard,
  AutoReloadCard,
  PaymentHistory,
  SettingsModal
} from './components';

const Billing = () => {
  const { user } = useGlobal();
  const { isDarkMode } = useTheme();
  const { token } = theme.useToken();
  const { t } = useTranslation();
  const { handleError } = useErrorHandler();

  // State management
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [payments, setPayments] = useState([]);
  const [products, setProducts] = useState([]);
  const [autoChargeSettings, setAutoChargeSettings] = useState(null);
  const [billingDetails, setBillingDetails] = useState(null);
  const [isSettingsVisible, setIsSettingsVisible] = useState(false);
  const [isPaymentDetailModalVisible, setIsPaymentDetailModalVisible] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION);

  // Card styling
  const cardStyle = {
    backgroundColor: token.colorBgContainer,
    color: token.colorText,
    border: isDarkMode ? `1px solid ${token.colorBorder}` : 'none',
  };

  // API calls
  const fetchPayments = async (page, size) => {
    try {
      setLoading(true);
      const response = await getPayments({ page, size });

      if (response.success) {
        const formattedPayments = formatPayments(response.data);

        setPayments(formattedPayments);
        setPagination({
          current: response.metadata.page,
          pageSize: response.metadata.per_page,
          total: response.metadata.total,
        });
      } else {
        message.error(t('failed_to_fetch_payment_history'));
      }
    } catch (error) {
      message.error(t('error_loading_payment_history'));
    } finally {
      setLoading(false);
    }
  };

  const fetchProductData = async () => {
    setLoading(true);
    try {
      const { data } = await getProducts();
      setProducts(data);
    } catch (error) {
      handleError(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAutoChargeSettings = async () => {
    try {
      const { data } = await getAutoChargeSettings();
      setAutoChargeSettings(data);
    } catch (error) {
      handleError(error);
    }
  };

  const fetchBillingDetails = async () => {
    try {
      const { data } = await getBillingDetails();
      setBillingDetails(data);
    } catch (error) {
      handleError(error);
    }
  };

  const handleUpdateSettings = async (values) => {
    try {
      const { data: updatedSettings } = await updateAutoChargeSettings(values);
      setAutoChargeSettings(updatedSettings);
      message.success(t('auto_reload_settings_updated'));
      setIsSettingsVisible(false);
    } catch (error) {
      handleError(error);
      message.error(t('failed_update_auto_reload'));
    }
  };

  // Event handlers
  const handleTableChange = (pagination) => {
    fetchPayments(pagination.current, pagination.pageSize);
  };

  const handleViewPaymentDetails = (payment) => {
    setSelectedPayment(payment);
    setIsPaymentDetailModalVisible(true);
  };

  const openSettingsModal = (event) => {
    event.stopPropagation();
    setIsSettingsVisible(true);
  };

  const closeSettingsModal = (event) => {
    event.stopPropagation();
    setIsSettingsVisible(false);
  };

  // Initial data loading
  useEffect(() => {
    fetchPayments(pagination.current, pagination.pageSize);
  }, []);

  useEffect(() => {
    fetchProductData();
  }, []);

  useEffect(() => {
    fetchBillingDetails();
  }, []);

  useEffect(() => {
    fetchAutoChargeSettings();
  }, []);

  console.log('autoChargeSettings', autoChargeSettings);
  return (
    <>
      {isSettingsVisible && (
        <SettingsModal
          isVisible={isSettingsVisible}
          onCancel={closeSettingsModal}
          onSubmit={handleUpdateSettings}
          autoChargeSettings={autoChargeSettings}
          products={products}
        />
      )}

      {isModalVisible && (
        <Plans
          handleClose={() => setIsModalVisible(false)}
          autoChargeSettings={autoChargeSettings}
        />
      )}

      {isPaymentDetailModalVisible && (
        <PaymentDetailModal
          payment={selectedPayment}
          visible={isPaymentDetailModalVisible}
          onClose={() => setIsPaymentDetailModalVisible(false)}
        />
      )}

      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={8}>
          <BalanceCard
            user={user}
            onAddCoins={() => setIsModalVisible(true)}
            cardStyle={cardStyle}
          />
        </Col>

        <Col xs={24} sm={12} lg={8}>
          <PaymentInfoCard
            billingDetails={billingDetails}
            cardStyle={cardStyle}
          />
        </Col>

        <Col xs={24} sm={24} lg={8}>
          <AutoReloadCard
            autoChargeSettings={autoChargeSettings}
            products={products}
            onOpenSettings={openSettingsModal}
            cardStyle={cardStyle}
          />
        </Col>
      </Row>

      <Row style={{ marginTop: '16px' }}>
        <Col span={24}>
          <PaymentHistory
            payments={payments}
            loading={loading}
            pagination={pagination}
            onTableChange={handleTableChange}
            onViewPaymentDetails={handleViewPaymentDetails}
            cardStyle={cardStyle}
          />
        </Col>
      </Row>
    </>
  );
};

export default Billing;
